import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Funnel, MagnifyingGlass } from "@phosphor-icons/react";

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const activeCategory = searchParams.get("category") || "";

  useEffect(() => {
    API.get("/categories")
      .then(r => setCategories(Array.isArray(r.data) ? r.data : []))
      .catch(err => console.error("Failed to load categories:", err));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCategory) params.category = activeCategory;
    if (search) params.search = search;
    API.get("/products", { params })
      .then(r => setProducts(Array.isArray(r.data) ? r.data : []))
      .catch(err => console.error("Failed to load products:", err))
      .finally(() => setLoading(false));
  }, [activeCategory, search]);

  const setCategory = (cat) => {
    if (cat) {
      setSearchParams({ category: cat });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="shop-page">
      {/* Header */}
      <div className="mb-16">
        <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3 font-medium">The Collection</p>
        <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#FDFBF7] tracking-tighter">
          {activeCategory ? categories.find(c => c.slug === activeCategory)?.name || "Shop" : "All Products"}
        </h1>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-6 mb-12 items-start md:items-center justify-between" data-testid="shop-filters">
        {/* Category tabs */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setCategory("")}
            className={`px-5 py-2 text-xs tracking-[0.15em] uppercase font-light transition-all duration-300 border ${
              activeCategory === ""
                ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                : "border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/30 hover:text-[#FDFBF7]"
            }`}
            data-testid="filter-all"
          >
            All Products
          </button>
          {categories.map(cat => (
            <button
              key={cat.slug}
              onClick={() => setCategory(cat.slug)}
              className={`px-5 py-2 text-xs tracking-[0.15em] uppercase font-light transition-all duration-300 border ${
                activeCategory === cat.slug
                  ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                  : "border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/30 hover:text-[#FDFBF7]"
              }`}
              data-testid={`filter-${cat.slug}`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative w-full md:w-64">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" weight="thin" />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#121212] border border-white/10 pl-10 pr-4 py-2.5 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37]/40 transition-colors duration-300 font-light"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="aspect-[3/4] bg-[#121212] animate-pulse border border-white/5" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-24">
          <p className="text-[#A1A1AA] text-sm font-light">No products found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
          {products.map((product, idx) => (
            <ProductCard key={product.id} product={product} index={idx} />
          ))}
        </div>
      )}
    </div>
  );
}
